import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { History, Target, Eye, Users, Award, Heart, Calendar, TrendingUp, Sparkles } from 'lucide-react';
import CenteredLogo from '../components/CenteredLogo';

const Despre = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end start'],
  });

  const y = useTransform(scrollYProgress, [0, 1], ['0%', '20%']);

  const stats = [
    { value: '2018', label: 'Anul fondării', icon: Calendar },
    { value: '8+', label: 'Ani de activitate', icon: TrendingUp },
    { value: '2', label: 'Servere active', icon: Sparkles },
    { value: '8000+', label: 'Membri în comunitate', icon: Users },
  ];

  const values = [
    {
      icon: Heart,
      title: 'Respect',
      description: 'Tratăm fiecare membru al comunității cu respect și considerație.',
      color: 'from-red-500 to-pink-500',
    },
    {
      icon: Award,
      title: 'Profesionalism',
      description: 'Fiecare server este configurat cu grijă și atenție la detalii.',
      color: 'from-yellow-500 to-orange-500',
    },
    {
      icon: Target,
      title: 'Calitate',
      description: 'Ne străduim să oferim cea mai bună experiență posibilă.',
      color: 'from-green-500 to-emerald-500',
    },
    {
      icon: Eye,
      title: 'Transparență',
      description: 'Suntem deschiși și onești în toate acțiunile noastre.',
      color: 'from-blue-500 to-cyan-500',
    },
  ];



  return (
    <div ref={containerRef} className="relative min-h-screen">
      {/* Parallax Background */}
      <motion.div
        style={{ y }}
        className="absolute inset-0 pointer-events-none overflow-hidden"
      >
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-[#fc6d00]/10 rounded-full blur-[150px]" />
      </motion.div>

      {/* Centered Logo - Desktop only */}
      <CenteredLogo />

      {/* Hero Section */}
      <section className="relative py-8 md:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6"
            >
              Despre{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#fc6d00] to-[#ff8c00]">
                SimplyGods
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-lg text-[#b3b3c2]"
            >
              O comunitate fondată în 2018, construită pe respect, seriozitate și pasiune pentru gaming.
            </motion.p>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-20"
          >
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.5 + index * 0.1 }}
                className="glass rounded-2xl p-6 text-center border border-white/10 hover:border-[#fc6d00]/30 transition-colors duration-300"
              >
                <stat.icon className="w-8 h-8 text-[#fc6d00] mx-auto mb-3" />
                <div className="text-3xl md:text-4xl font-bold text-white mb-1">{stat.value}</div>
                <div className="text-sm text-[#b3b3c2]">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>

          {/* Main Content */}
          <div className="grid lg:grid-cols-2 gap-8 mb-20">
            {/* History */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="glass rounded-2xl p-6 md:p-8 border border-white/10"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl bg-[#fc6d00]/20 flex items-center justify-center">
                  <History className="w-6 h-6 text-[#fc6d00]" />
                </div>
                <h2 className="text-2xl font-semibold">Istoria Noastră</h2>
              </div>

              <div className="space-y-4">
                <p className="text-[#b3b3c2] leading-relaxed">
                  <span className="text-white font-medium">SimplyGods</span> a început în 2018 ca un proiect 
                  mic, dar ambițios, creat de <span className="text-[#fc6d00]">EpicShorty</span>. 
                  De-a lungul anilor, comunitatea a crescut organic, atrăgând jucători serioși și pasionați.
                </p>
                
                <p className="text-[#b3b3c2] leading-relaxed">
                  În prezent, SimplyGods este una dintre cele mai vechi comunități active din România, 
                  cu servere moderne, staff dedicat și o viziune pe termen lung.
                </p>
              </div>
            </motion.div>

            {/* Vision */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="glass rounded-2xl p-6 md:p-8 border border-white/10"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl bg-[#fc6d00]/20 flex items-center justify-center">
                  <Eye className="w-6 h-6 text-[#fc6d00]" />
                </div>
                <h2 className="text-2xl font-semibold">Viziunea Noastră</h2>
              </div>

              <div className="space-y-4">
                <p className="text-[#b3b3c2] leading-relaxed">
                  Ne dorim să construim o comunitate <span className="text-white font-medium">unită, matură și activă</span>, 
                  unde fiecare jucător se simte respectat și valorizat.
                </p>
                
                <p className="text-[#b3b3c2] leading-relaxed">
                  Proiectele noastre sunt gândite pe termen lung, cu actualizări constante și implicare reală 
                  din partea echipei noastre.
                </p>
              </div>
            </motion.div>
          </div>

          {/* Values */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-20"
          >
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Valorile Noastre</h2>
              <p className="text-[#b3b3c2] max-w-2xl mx-auto">
                Ne bazăm pe principii solide care ne ghidează în tot ceea ce facem.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {values.map((value, index) => (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                  className="group relative p-6 rounded-2xl bg-[#111119] border border-[#262636] hover:border-[#fc6d00]/50 transition-all duration-300"
                >
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${value.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                    <value.icon className="w-6 h-6 text-white" />
                  </div>
                  
                  <h3 className="text-lg font-semibold mb-2">{value.title}</h3>
                  <p className="text-sm text-[#b3b3c2] leading-relaxed">{value.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

        </div>
      </section>
    </div>
  );
};

export default Despre;
